import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { deviationTrackingAtom } from '@/atoms/deviation-progress.atom';
import { reportTrackingAtom } from '@/atoms/report-progress.atom';
import { execute } from '@/graphql/execute';
import type { LayersQuery } from '@/graphql/graphql';
import { useDialog } from '@/providers/DialogProvider';
import { startDeviationAnalysis } from '@/queries/deviation.query';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NumericField } from '@/components/ui/numeric-field';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type LayerData = LayersQuery['layers'][number];

interface Props {
  sourceLayer: LayerData;
  candidateReferenceLayers: LayerData[];
  projectId: string;
  initialValues?: Partial<FormData>;
}

interface FormData {
  name: string;
  referenceLayerId: string;
  mode: 'vertical' | 'normal' | 'nearest' | 'horizontal';
  min: number;
  max: number;
  decimate: number;
  colormap: string;
  verticalThreshold: number;
  kNearestTriangles: number;
  signed: boolean;
  ifcName?: string;
  ifcTypes?: string;
  outTif: boolean;
  tifResolution: number;
  tifStat: 'mean' | 'min' | 'max' | 'count';
  /** Optional EPSG override for the output GeoTIFF's CRS. Empty = let the
   *  server fill in the project's own SRID (the normal case). Useful only
   *  when the project SRID is wrong/missing and you want to stamp a
   *  specific code without a project edit. */
  tifSrid: number | null;
  renderPerSurface: boolean;
  renderImageSize: number;
  renderColormap: string;
  volumeStats: boolean;
  volumeResolution: number;
  volumeTolerance: number;
  volumePlane: 'xy' | 'fitted';
  referenceMode: 'tin' | 'surfaces';
  referenceResolution: number;
  referenceCellStat: 'min' | 'mean' | 'median' | 'p05';
  referenceInterp: 'nearest' | 'bilinear';
  generateReport: boolean;
  reportTitle?: string;
  reportNotes?: string;
  reportLocale: 'en' | 'de' | 'es' | 'fr' | 'hu';
}

export function RunDeviationAnalysisDialog({ sourceLayer, candidateReferenceLayers, projectId, initialValues }: Props) {
  const { t, i18n } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();
  const queryClient = useQueryClient();
  const [, setTrackers] = useAtom(deviationTrackingAtom);
  const [, setReportTrackers] = useAtom(reportTrackingAtom);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const SUPPORTED_LOCALES = ['en', 'de', 'es', 'fr', 'hu'] as const;
  type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

  const formSchema = useMemo(
    () =>
      z
        .object({
          name: z.string().min(1),
          referenceLayerId: z.string().min(1, t('deviationAnalysis.referenceLayerRequired')),
          mode: z.enum(['vertical', 'normal', 'nearest', 'horizontal']),
          min: z.number(),
          max: z.number(),
          decimate: z.number().int().min(1).default(1),
          colormap: z.string().default('Spectral_r'),
          verticalThreshold: z.number().min(0).max(1).default(0.25),
          kNearestTriangles: z.number().int().min(1).default(16),
          signed: z.boolean().default(true),
          ifcName: z.string().optional(),
          ifcTypes: z.string().optional(),
          outTif: z.boolean().default(false),
          tifResolution: z.number().positive().default(0.05),
          tifStat: z.enum(['mean', 'min', 'max', 'count']).default('mean'),
          tifSrid: z.number().int().positive().nullable().default(null),
          renderPerSurface: z.boolean().default(true),
          renderImageSize: z.number().int().min(256).max(8192).default(2048),
          renderColormap: z.string().default('Spectral_r'),
          volumeStats: z.boolean().default(true),
          volumeResolution: z.number().positive().default(0.05),
          volumeTolerance: z.number().positive().default(0.01),
          volumePlane: z.enum(['xy', 'fitted']).default('xy'),
          referenceMode: z.enum(['tin', 'surfaces']).default('surfaces'),
          referenceResolution: z.number().positive().default(0.05),
          referenceCellStat: z.enum(['min', 'mean', 'median', 'p05']).default('min'),
          referenceInterp: z.enum(['nearest', 'bilinear']).default('nearest'),
          generateReport: z.boolean().default(false),
          reportTitle: z.string().optional(),
          reportNotes: z.string().optional(),
          reportLocale: z.enum(['en', 'de', 'es', 'fr', 'hu']).default('en'),
        })
        .refine((d) => d.min < d.max, {
          message: t('deviationAnalysis.minMustBeLessThanMax'),
          path: ['max'],
        }),
    [t],
  );

  // Smart default for `referenceMode`. VECTOR layers (GeoJSON polygons) are
  // typically several walls / surfaces — `surfaces` mode produces a CSV
  // breakdown and per-surface renders. MODEL layers (DXF / IFC) are
  // typically one continuous TIN — `tin` mode does a single integration.
  const defaultReferenceMode: 'tin' | 'surfaces' = candidateReferenceLayers[0]?.type === 'VECTOR' ? 'surfaces' : 'tin';

  const defaultReportLocale: SupportedLocale = (SUPPORTED_LOCALES as readonly string[]).includes(i18n.language)
    ? (i18n.language as SupportedLocale)
    : 'en';

  const defaultName = `${sourceLayer.name} vs ${candidateReferenceLayers[0]?.name ?? ''}`.trim();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: defaultName,
      referenceLayerId: candidateReferenceLayers[0]?.id ?? '',
      mode: 'vertical',
      min: -0.05,
      max: 0.05,
      decimate: 1,
      colormap: 'Spectral_r',
      verticalThreshold: 0.25,
      kNearestTriangles: 16,
      signed: true,
      ifcName: '',
      ifcTypes: '',
      outTif: false,
      tifResolution: 0.05,
      tifStat: 'mean',
      tifSrid: null,
      renderPerSurface: true,
      renderImageSize: 2048,
      renderColormap: 'Spectral_r',
      volumeStats: true,
      volumeResolution: 0.05,
      volumeTolerance: 0.01,
      volumePlane: 'xy',
      referenceMode: defaultReferenceMode,
      referenceResolution: 0.05,
      referenceCellStat: 'min',
      referenceInterp: 'nearest',
      generateReport: false,
      reportTitle: defaultName,
      reportNotes: '',
      reportLocale: defaultReportLocale,
      ...initialValues,
    },
  });

  // Re-default the referenceMode when the user picks a different reference
  // layer — VECTOR → surfaces, MODEL → tin. Only applies if the user hasn't
  // explicitly touched referenceMode (Form's `formState.dirtyFields`); we
  // don't want to clobber a deliberate override.
  const watchedReferenceLayerId = form.watch('referenceLayerId');
  useEffect(() => {
    const refLayer = candidateReferenceLayers.find((l) => l.id === watchedReferenceLayerId);
    if (!refLayer) return;
    if (refLayer.type === 'POINTCLOUD') {
      // PC references only support vertical mode + tin reference-mode.
      if (form.getValues('mode') !== 'vertical') {
        form.setValue('mode', 'vertical', { shouldDirty: false });
      }
      if (form.getValues('referenceMode') !== 'tin') {
        form.setValue('referenceMode', 'tin', { shouldDirty: false });
      }
      // PC<>PC is the earthworks / change-detection use case where the
      // tolerance-band defaults (±5 cm) are too narrow — typical "removed
      // material" deviations land in the ±0.5–2 m range and a tight band
      // saturates the colorbar, making visualisation misleading. Widen
      // when the user picks a PC reference and hasn't already set it.
      if (!form.formState.dirtyFields.min && form.getValues('min') === -0.05) {
        form.setValue('min', -1.0, { shouldDirty: false });
      }
      if (!form.formState.dirtyFields.max && form.getValues('max') === 0.05) {
        form.setValue('max', 1.0, { shouldDirty: false });
      }
      // Same logic for the volume tolerance: 1 cm is far too tight for
      // earthworks scale; 5 cm is a more realistic "is this within the
      // build envelope" threshold.
      if (!form.formState.dirtyFields.volumeTolerance && form.getValues('volumeTolerance') === 0.01) {
        form.setValue('volumeTolerance', 0.05, { shouldDirty: false });
      }
      return;
    }
    if (form.formState.dirtyFields.referenceMode) return;
    const next: 'tin' | 'surfaces' = refLayer.type === 'VECTOR' ? 'surfaces' : 'tin';
    if (form.getValues('referenceMode') !== next) {
      form.setValue('referenceMode', next, { shouldDirty: false });
    }
  }, [watchedReferenceLayerId, candidateReferenceLayers, form]);

  // Update the suggested name when the reference layer changes, but only if
  // the user hasn't manually edited the name field.
  useEffect(() => {
    if (form.formState.dirtyFields.name) return;
    const refLayer = candidateReferenceLayers.find((l) => l.id === watchedReferenceLayerId);
    if (!refLayer) return;
    const suggested = `${sourceLayer.name} vs ${refLayer.name}`;
    if (form.getValues('name') !== suggested) {
      form.setValue('name', suggested, { shouldDirty: false });
    }
  }, [watchedReferenceLayerId, candidateReferenceLayers, form, sourceLayer.name]);

  const mutation = useMutation({
    mutationFn: async (vars: FormData) => {
      // Only send `referenceMode` if the user explicitly picked it. The
      // form has a smart UI-default per layer type (VECTOR → surfaces,
      // MODEL → tin) so the dropdown shows something sensible, but if
      // the user never touched it we want the SERVER to apply its
      // feature-count-aware default — otherwise a TIN stored as a 4000-
      // feature GeoJSON gets routed to surfaces mode and triggers the
      // per-surface render cap. Sending `undefined` here lets preprocess
      // count features and pick `tin` automatically.
      const referenceModeDirty = !!form.formState.dirtyFields.referenceMode;
      // Only forward the IFC filters if the chosen reference layer is actually
      // an IFC model — otherwise stale form values from a previously selected
      // IFC reference would silently end up in the request.
      const submittedRefLayer = candidateReferenceLayers.find((l) => l.id === vars.referenceLayerId);
      const submittedRefIsIfc =
        submittedRefLayer?.type === 'MODEL' &&
        ['IFC', 'FRAGMENT'].includes((submittedRefLayer.metadata as { type?: string } | null)?.type ?? '');
      const submittedRefIsPc = submittedRefLayer?.type === 'POINTCLOUD';
      // When generating a PDF report, per-surface renders must be enabled so
      // the report's image section has content. Force it true regardless of
      // what the user has set for renderPerSurface in the form.
      const effectiveRenderPerSurface = vars.generateReport ? true : vars.renderPerSurface;
      return execute(startDeviationAnalysis, {
        input: {
          sourceLayerId: sourceLayer.id,
          referenceLayerId: vars.referenceLayerId,
          name: vars.name || undefined,
          params: {
            mode: vars.mode,
            min: vars.min,
            max: vars.max,
            decimate: vars.decimate,
            colormap: vars.colormap,
            verticalThreshold: vars.verticalThreshold,
            kNearestTriangles: vars.kNearestTriangles,
            signed: vars.signed,
            ifcName: submittedRefIsIfc ? vars.ifcName || undefined : undefined,
            ifcTypes: submittedRefIsIfc ? vars.ifcTypes || undefined : undefined,
            outTif: vars.outTif,
            tifResolution: vars.tifResolution,
            tifStat: vars.tifStat,
            // null → server fills with project SRID. Sending undefined here
            // would also work but explicit null reads more deliberately.
            tifSrid: vars.tifSrid ?? null,
            renderPerSurface: effectiveRenderPerSurface,
            renderImageSize: vars.renderImageSize,
            renderColormap: vars.renderColormap,
            volumeStats: vars.volumeStats,
            volumeResolution: vars.volumeResolution,
            volumeTolerance: vars.volumeTolerance,
            volumePlane: vars.volumePlane,
            referenceMode: referenceModeDirty ? vars.referenceMode : undefined,
            referenceResolution: submittedRefIsPc ? vars.referenceResolution : undefined,
            referenceCellStat: submittedRefIsPc ? vars.referenceCellStat : undefined,
            referenceInterp: submittedRefIsPc ? vars.referenceInterp : undefined,
          },
          reportOptions: vars.generateReport
            ? {
                generate: true,
                title: vars.reportTitle?.trim() || undefined,
                notes: vars.reportNotes?.trim() || undefined,
                locale: vars.reportLocale,
              }
            : undefined,
        },
      });
    },
    onSuccess: (data, vars) => {
      const batchId = data.startDeviationAnalysis;
      const toastId = toast.loading(t('deviationAnalysis.runningToast', { name: sourceLayer.name }), {
        duration: Infinity,
      });
      // Snapshot the layer IDs already derived from this source. The hook
      // resolves the toast when a NEW derived layer (one whose id isn't in
      // this set) appears — this is robust to client/server clock skew, which
      // bit us with the previous `createdAt >= startedAt` comparison. Pulled
      // straight from the React Query cache so we don't have to re-fetch.
      const cachedLayers = queryClient.getQueryData<{ layers: LayerData[] }>(['layers', projectId]);
      const existingDerivedLayerIds = (cachedLayers?.layers ?? [])
        .filter((l) => {
          const meta = (l.metadata ?? {}) as { derivedFrom?: string };
          return meta.derivedFrom === sourceLayer.id;
        })
        .map((l) => l.id);
      setTrackers((prev) => [
        ...prev,
        {
          toastId,
          batchId,
          projectId,
          sourceLayerId: sourceLayer.id,
          sourceLayerName: sourceLayer.name,
          existingDerivedLayerIds,
        },
      ]);
      // If the user opted in to a PDF report, push a report tracker so
      // useReportProgress can surface a download toast when the chained
      // PDF_REPORT job completes — but do NOT show a "Generating PDF report"
      // toast: the deviation toast above already covers "in flight" and a
      // second running toast for the same batch is noise. The success toast
      // (with Download action) still appears via useReportProgress because
      // we register the batchId here.
      if (vars.generateReport) {
        const reportTitle = vars.reportTitle?.trim() || vars.name;
        setReportTrackers((prev) => [
          ...prev,
          { toastId: undefined, batchId, projectId, title: reportTitle },
        ]);
      }
      dismissActiveDialog();
    },
    onError: (err: Error) => {
      toast.error(err.message || t('deviationAnalysis.failedToStart'));
    },
  });

  const watchedMode = form.watch('mode');
  const isHorizontal = watchedMode === 'horizontal';
  const isNearest = watchedMode === 'nearest';
  const watchedGenerateReport = form.watch('generateReport');
  // PDF report is gated on volume stats — without `volumeStats: true` the
  // deviation worker doesn't compute volume.* fields, so the report would
  // show '—' for every metric. If the user turns volume stats off while
  // generateReport is checked, auto-uncheck it so the submission isn't
  // silently broken.
  const watchedVolumeStats = form.watch('volumeStats');
  useEffect(() => {
    if (!watchedVolumeStats && form.getValues('generateReport')) {
      form.setValue('generateReport', false, { shouldDirty: true });
    }
  }, [watchedVolumeStats, form]);

  // IFC element-name / element-type filters only make sense when the reference
  // layer is an IFC model. The backend stores converted IFC files with
  // `metadata.type === 'FRAGMENT'`; some legacy uploads still use 'IFC'.
  const watchedReferenceLayer = candidateReferenceLayers.find((l) => l.id === watchedReferenceLayerId);
  const referenceLayerIsIfc =
    watchedReferenceLayer?.type === 'MODEL' &&
    (watchedReferenceLayer.metadata as { type?: string } | null)?.type !== undefined &&
    ['IFC', 'FRAGMENT'].includes((watchedReferenceLayer.metadata as { type: string }).type);
  const referenceLayerIsPointcloud = watchedReferenceLayer?.type === 'POINTCLOUD';

  if (candidateReferenceLayers.length === 0) {
    return (
      <>
        <DialogHeader>
          <DialogTitle>{t('deviationAnalysis.title')}</DialogTitle>
        </DialogHeader>
        <p className='py-4 text-sm text-slate-700 dark:text-stone-200'>{t('deviationAnalysis.noReferenceLayers')}</p>
        <DialogFooter>
          <Button variant='outline' onClick={() => dismissActiveDialog()}>
            {t('common.close', { defaultValue: 'Close' })}
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <>
      {/* `min-w-0` on the header lets its grid track shrink below the
          intrinsic width of a very long source-layer name — without it,
          the grid's default `min-width: auto` pegs the dialog's width to
          the name and the form inputs overflow. `break-words` then takes
          effect on the description text. */}
      <DialogHeader className='min-w-0'>
        <DialogTitle>{t('deviationAnalysis.title')}</DialogTitle>
        <DialogDescription className='break-words'>
          {t('deviationAnalysis.sourceLabel')}: <strong>{sourceLayer.name}</strong>
        </DialogDescription>
      </DialogHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit((vars) => mutation.mutate(vars))} className='flex max-h-[80vh] flex-col'>
          <div className='flex-1 space-y-4 overflow-y-auto px-1 py-1'>
            <FormField
              control={form.control}
              name='name'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('deviationAnalysis.outputName')}</FormLabel>
                  <FormControl>
                    <Input type='text' {...field} />
                  </FormControl>
                  <FormDescription>{t('deviationAnalysis.outputNameHelp')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='referenceLayerId'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('deviationAnalysis.referenceLayer')}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {candidateReferenceLayers.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name} ({l.type})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* referenceMode is conceptually paired with the reference layer:
              one continuous TIN vs N independent surfaces. Auto-defaults
              from the reference layer's type (VECTOR → surfaces, MODEL →
              tin) and stays out of the way unless the user overrides.
              Hidden for POINTCLOUD references — those force tin-mode. */}
            {!referenceLayerIsPointcloud && (
              <FormField
                control={form.control}
                name='referenceMode'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('deviationAnalysis.referenceMode')}</FormLabel>
                    <Select
                      onValueChange={(v) => {
                        // Mark the field dirty so the layer-change effect
                        // doesn't clobber the user's explicit choice next
                        // time they swap reference layers.
                        field.onChange(v);
                      }}
                      value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value='surfaces'>{t('deviationAnalysis.referenceModes.surfaces')}</SelectItem>
                        <SelectItem value='tin'>{t('deviationAnalysis.referenceModes.tin')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {field.value === 'surfaces'
                        ? t('deviationAnalysis.referenceModeHelp.surfaces')
                        : t('deviationAnalysis.referenceModeHelp.tin')}
                    </FormDescription>
                  </FormItem>
                )}
              />
            )}

            {referenceLayerIsPointcloud ? (
              <div className='rounded border border-slate-200 p-3 text-sm text-slate-700 dark:border-stone-700 dark:text-stone-200'>
                {t('deviationAnalysis.modeLockedToVerticalForPc')}
              </div>
            ) : (
              <FormField
                control={form.control}
                name='mode'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('deviationAnalysis.mode')}</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className='flex flex-col gap-2'>
                        {(['vertical', 'normal', 'nearest', 'horizontal'] as const).map((m) => (
                          <label key={m} className='flex cursor-pointer items-start gap-2'>
                            <RadioGroupItem value={m} id={`mode-${m}`} />
                            <div>
                              <div className='text-sm font-medium'>{t(`deviationAnalysis.modes.${m}.label`)}</div>
                              <div className='text-xs text-slate-600 dark:text-stone-400'>
                                {t(`deviationAnalysis.modes.${m}.description`)}
                              </div>
                            </div>
                          </label>
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            <div className='grid grid-cols-2 gap-3'>
              <FormField
                control={form.control}
                name='min'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('deviationAnalysis.min')}</FormLabel>
                    <FormControl>
                      <NumericField step='0.001' value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='max'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('deviationAnalysis.max')}</FormLabel>
                    <FormControl>
                      <NumericField step='0.001' value={field.value} onChange={field.onChange} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name='decimate'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('deviationAnalysis.decimate')}</FormLabel>
                  <FormControl>
                    <Input
                      type='number'
                      step='1'
                      min='1'
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 1)}
                    />
                  </FormControl>
                  <FormDescription>{t('deviationAnalysis.decimateHelp')}</FormDescription>
                </FormItem>
              )}
            />

            <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
              <CollapsibleTrigger asChild>
                <Button variant='ghost' type='button' className='w-full justify-between'>
                  <span>{t('deviationAnalysis.advanced')}</span>
                  <ChevronDown className={`size-4 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className='space-y-3 pt-2'>
                {isHorizontal && (
                  <FormField
                    control={form.control}
                    name='verticalThreshold'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('deviationAnalysis.verticalThreshold')}</FormLabel>
                        <FormControl>
                          <Input
                            type='number'
                            step='0.05'
                            min='0'
                            max='1'
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value))}
                          />
                        </FormControl>
                        <FormDescription>{t('deviationAnalysis.verticalThresholdHelp')}</FormDescription>
                      </FormItem>
                    )}
                  />
                )}
                <FormField
                  control={form.control}
                  name='kNearestTriangles'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('deviationAnalysis.kNearestTriangles')}</FormLabel>
                      <FormControl>
                        <Input
                          type='number'
                          step='1'
                          min='1'
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 16)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                {isNearest && (
                  <FormField
                    control={form.control}
                    name='signed'
                    render={({ field }) => (
                      <FormItem className='flex items-center gap-2'>
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className='!mt-0'>{t('deviationAnalysis.signed')}</FormLabel>
                      </FormItem>
                    )}
                  />
                )}
                {referenceLayerIsIfc && (
                  <>
                    <FormField
                      control={form.control}
                      name='ifcName'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('deviationAnalysis.ifcName')}</FormLabel>
                          <FormControl>
                            <Input type='text' placeholder={t('deviationAnalysis.ifcNamePlaceholder')} {...field} />
                          </FormControl>
                          <FormDescription>{t('deviationAnalysis.ifcNameHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='ifcTypes'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('deviationAnalysis.ifcTypes')}</FormLabel>
                          <FormControl>
                            <Input type='text' placeholder={t('deviationAnalysis.ifcTypesPlaceholder')} {...field} />
                          </FormControl>
                          <FormDescription>{t('deviationAnalysis.ifcTypesHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                  </>
                )}

                {referenceLayerIsPointcloud && (
                  <div className='border-t border-slate-200/70 pt-3 dark:border-stone-700/40'>
                    <div className='mb-2 text-sm font-medium'>{t('deviationAnalysis.pcReferenceSection')}</div>
                    <div className='grid grid-cols-2 gap-3'>
                      <FormField
                        control={form.control}
                        name='referenceResolution'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('deviationAnalysis.referenceResolution')}</FormLabel>
                            <FormControl>
                              <Input
                                type='number'
                                step='any'
                                {...field}
                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0.05)}
                              />
                            </FormControl>
                            <FormDescription>{t('deviationAnalysis.referenceResolutionHelp')}</FormDescription>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name='referenceCellStat'
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('deviationAnalysis.referenceCellStat')}</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value='min'>{t('deviationAnalysis.referenceCellStats.min')}</SelectItem>
                                <SelectItem value='mean'>{t('deviationAnalysis.referenceCellStats.mean')}</SelectItem>
                                <SelectItem value='median'>
                                  {t('deviationAnalysis.referenceCellStats.median')}
                                </SelectItem>
                                <SelectItem value='p05'>{t('deviationAnalysis.referenceCellStats.p05')}</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>{t('deviationAnalysis.referenceCellStatHelp')}</FormDescription>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name='referenceInterp'
                        render={({ field }) => (
                          <FormItem className='col-span-2'>
                            <FormLabel>{t('deviationAnalysis.referenceInterp')}</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value='nearest'>
                                  {t('deviationAnalysis.referenceInterps.nearest')}
                                </SelectItem>
                                <SelectItem value='bilinear'>
                                  {t('deviationAnalysis.referenceInterps.bilinear')}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            <FormDescription>{t('deviationAnalysis.referenceInterpHelp')}</FormDescription>
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                )}

                <div className='border-t border-slate-200/70 pt-3 dark:border-stone-700/40'>
                  <FormField
                    control={form.control}
                    name='outTif'
                    render={({ field }) => (
                      <FormItem className='flex items-center gap-2'>
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className='!mt-0'>{t('deviationAnalysis.outTif')}</FormLabel>
                      </FormItem>
                    )}
                  />
                  <FormDescription className='mt-1'>{t('deviationAnalysis.outTifHelp')}</FormDescription>
                </div>

                {form.watch('outTif') && (
                  <div className='grid grid-cols-2 gap-3'>
                    <FormField
                      control={form.control}
                      name='tifResolution'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('deviationAnalysis.tifResolution')}</FormLabel>
                          <FormControl>
                            <Input
                              type='number'
                              step='any'
                              {...field}
                              onChange={(e) => field.onChange(parseFloat(e.target.value) || 0.05)}
                            />
                          </FormControl>
                          <FormDescription>{t('deviationAnalysis.tifResolutionHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='tifStat'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('deviationAnalysis.tifStat')}</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value='mean'>{t('deviationAnalysis.tifStats.mean')}</SelectItem>
                              <SelectItem value='max'>{t('deviationAnalysis.tifStats.max')}</SelectItem>
                              <SelectItem value='min'>{t('deviationAnalysis.tifStats.min')}</SelectItem>
                              <SelectItem value='count'>{t('deviationAnalysis.tifStats.count')}</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription>{t('deviationAnalysis.tifStatHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='tifSrid'
                      render={({ field }) => (
                        <FormItem className='col-span-2'>
                          <FormLabel>{t('deviationAnalysis.tifSrid')}</FormLabel>
                          <FormControl>
                            {/* Empty input → null → server falls back to the
                              project's own SRID, which is the right answer
                              99% of the time. Only fill this in when you
                              specifically want to override (e.g. project's
                              SRID is wrong and you can't edit it right now). */}
                            <Input
                              type='number'
                              step='1'
                              min='1'
                              placeholder={t('deviationAnalysis.tifSridPlaceholder')}
                              value={field.value ?? ''}
                              onChange={(e) => {
                                const v = e.target.value.trim();
                                if (v === '') {
                                  field.onChange(null);
                                } else {
                                  const n = parseInt(v, 10);
                                  field.onChange(Number.isFinite(n) && n > 0 ? n : null);
                                }
                              }}
                            />
                          </FormControl>
                          <FormDescription>{t('deviationAnalysis.tifSridHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                <div className='border-t border-slate-200/70 pt-3 dark:border-stone-700/40'>
                  <FormField
                    control={form.control}
                    name='renderPerSurface'
                    render={({ field }) => (
                      <FormItem className='flex items-center gap-2'>
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className='!mt-0'>
                          {referenceLayerIsPointcloud
                            ? t('deviationAnalysis.renderOverviewPc')
                            : t('deviationAnalysis.renderPerSurface')}
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                  <FormDescription className='mt-1'>
                    {referenceLayerIsPointcloud
                      ? t('deviationAnalysis.renderOverviewPcHelp')
                      : t('deviationAnalysis.renderPerSurfaceHelp')}
                  </FormDescription>
                </div>

                {form.watch('renderPerSurface') && (
                  <div className='grid grid-cols-2 gap-3'>
                    <FormField
                      control={form.control}
                      name='renderImageSize'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('deviationAnalysis.renderImageSize')}</FormLabel>
                          <FormControl>
                            <Input
                              type='number'
                              step='256'
                              min='256'
                              max='8192'
                              {...field}
                              onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 2048)}
                            />
                          </FormControl>
                          <FormDescription>{t('deviationAnalysis.renderImageSizeHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='renderColormap'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('deviationAnalysis.renderColormap')}</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value='Spectral_r'>Spectral</SelectItem>
                              <SelectItem value='jet'>jet</SelectItem>
                              <SelectItem value='RdBu'>RdBu</SelectItem>
                              <SelectItem value='coolwarm'>coolwarm</SelectItem>
                              <SelectItem value='viridis'>viridis</SelectItem>
                              <SelectItem value='plasma'>plasma</SelectItem>
                              <SelectItem value='turbo'>turbo</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription>{t('deviationAnalysis.renderColormapHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                <div className='border-t border-slate-200/70 pt-3 dark:border-stone-700/40'>
                  <FormField
                    control={form.control}
                    name='volumeStats'
                    render={({ field }) => (
                      <FormItem className='flex items-center gap-2'>
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className='!mt-0'>{t('deviationAnalysis.volumeStats')}</FormLabel>
                      </FormItem>
                    )}
                  />
                  <FormDescription className='mt-1'>{t('deviationAnalysis.volumeStatsHelp')}</FormDescription>
                </div>

                {form.watch('volumeStats') && (
                  <div className='grid grid-cols-2 gap-3'>
                    <FormField
                      control={form.control}
                      name='volumeResolution'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('deviationAnalysis.volumeResolution')}</FormLabel>
                          <FormControl>
                            {/* `step="any"` disables the browser's "value must
                              match min + N*step" check. Without it, a min of
                              0.001 + step of 0.01 made 0.05 itself invalid
                              and only a sparse set of "weird" values worked.
                              Zod's `.positive()` is the real validator. */}
                            <Input
                              type='number'
                              step='any'
                              {...field}
                              onChange={(e) => field.onChange(parseFloat(e.target.value) || 0.05)}
                            />
                          </FormControl>
                          <FormDescription>{t('deviationAnalysis.volumeResolutionHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='volumeTolerance'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('deviationAnalysis.volumeTolerance')}</FormLabel>
                          <FormControl>
                            <Input
                              type='number'
                              step='any'
                              {...field}
                              onChange={(e) => field.onChange(parseFloat(e.target.value) || 0.01)}
                            />
                          </FormControl>
                          <FormDescription>{t('deviationAnalysis.volumeToleranceHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name='volumePlane'
                      render={({ field }) => (
                        <FormItem className='col-span-2'>
                          <FormLabel>{t('deviationAnalysis.volumePlane')}</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value='xy'>{t('deviationAnalysis.volumePlanes.xy')}</SelectItem>
                              <SelectItem value='fitted'>{t('deviationAnalysis.volumePlanes.fitted')}</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription>{t('deviationAnalysis.volumePlaneHelp')}</FormDescription>
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* PDF report opt-in — gated on volume stats being enabled
                (the report's tables/colourbar/etc. require volume.* fields). */}
            <div className='border-t border-slate-200/70 pt-3 dark:border-stone-700/40'>
              <FormField
                control={form.control}
                name='generateReport'
                render={({ field }) => (
                  <FormItem className={`flex items-center gap-2 ${watchedVolumeStats ? '' : 'opacity-50'}`}>
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={!watchedVolumeStats}
                      />
                    </FormControl>
                    <FormLabel className='!mt-0'>{t('report.alsoGenerate')}</FormLabel>
                  </FormItem>
                )}
              />
              {!watchedVolumeStats && (
                <p className='mt-1 text-xs text-slate-600 dark:text-stone-400'>
                  {t('report.requiresVolumeStats')}
                </p>
              )}
              {watchedGenerateReport && (
                <p className='mt-1 text-xs text-slate-600 dark:text-stone-400'>{t('report.alsoGenerateHint')}</p>
              )}
            </div>

            {watchedGenerateReport && (
              <div className='space-y-3'>
                <FormField
                  control={form.control}
                  name='reportTitle'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('report.titleLabel')}</FormLabel>
                      <FormControl>
                        <Input type='text' {...field} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='reportNotes'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('report.notesLabel')}</FormLabel>
                      <FormControl>
                        <Input type='text' {...field} value={field.value ?? ''} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name='reportLocale'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('report.languageLabel')}</FormLabel>
                      <FormControl>
                        <select
                          className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
                          {...field}>
                          <option value='en'>English</option>
                          <option value='de'>Deutsch</option>
                          <option value='es'>Español</option>
                          <option value='fr'>Français</option>
                          <option value='hu'>Magyar</option>
                        </select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>

          <DialogFooter className='mt-4 shrink-0 border-t border-slate-200/70 pt-4 dark:border-stone-700/40'>
            <Button variant='outline' type='button' onClick={() => dismissActiveDialog()} disabled={mutation.isPending}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type='submit' disabled={mutation.isPending}>
              {mutation.isPending ? t('deviationAnalysis.starting') : t('deviationAnalysis.run')}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}
